import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { appInfo } from "../config/appInfo.const";
import { getSecureItem, setSecureItem, removeSecureItem } from "../utils/LocalStorageManager";
import { apiUrls } from "../config/apiUrls.const";

const AUTH_URL: any = appInfo.authUrl;

const apiClient = axios.create({
  baseURL: AUTH_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  withCredentials: true
});

// Flag to track token generation requests
let isRefreshing = false;
// Queue to hold requests that fail while token is refreshing
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (token) {
      prom.resolve(token);
    } else {
      prom.reject(error);
    }
  });
  failedQueue = [];
};

// Request interceptor to attach Bearer token
apiClient.interceptors.request.use((config) => {
  const token = getSecureItem("access_token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, (error) => Promise.reject(error));

// Response interceptor with sliding-window automatic refresh engine
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response) {
      // Handle 401 Unauthorized errors (Token expired)
      if (error.response.status === 401 && !originalRequest._retry) {
        
        // If the route that failed is the actual login or refresh endpoint, do not retry
        if (originalRequest.url === apiUrls.login || originalRequest.url === apiUrls.refresh) {
          return Promise.reject(error);
        }

        if (isRefreshing) {
          // Queue this request while waiting for the token renewal
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return apiClient(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        const currentRefreshToken = getSecureItem("refresh_token");

        try {
          // Hit your backend token refresh route using standard raw axios instance 
          // to bypass global header injection loops
          const response = await axios.post(
            `${AUTH_URL}${apiUrls.refresh || "/refresh"}`, 
            {}, 
            {
              headers: {
                Authorization: `Bearer ${currentRefreshToken}`,
                Accept: "application/json",
              },
            }
          );

          const loginData = response.data;
          const newAccessToken = loginData?.data?.token?.access_token;
          const newRefreshToken = loginData?.data?.token?.refresh_token;

          if (newAccessToken) {
            setSecureItem("access_token", newAccessToken);
            setSecureItem("refresh_token", newRefreshToken || newAccessToken);

            processQueue(null, newAccessToken);
            
            // Re-run the initial failed request with the new active token
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            return apiClient(originalRequest);
          }
        } catch (refreshError) {
          processQueue(refreshError, null);
          
          // Clear credentials and force application context reload if the refresh token is also invalid/expired
          console.error("Refresh token expired or invalid. Forcing logout.");
          removeSecureItem("access_token");
          removeSecureItem("refresh_token");
          removeSecureItem("user");
          removeSecureItem("businesses");
          
          window.location.href = "/";
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      if (error.response.status === 403) {
        console.error("Forbidden: You don’t have access to this resource.");
      }
      if (error.response.status >= 500) {
        console.error("Server error, please try again later.");
      }
    }
    return Promise.reject(error);
  }
);

export default apiClient;

// Reusable HTTP methods
const _get = async <T = any>(url: string, params?: Record<string, any>, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
  return apiClient.get<T>(url, { ...config, params });
};
const _post = async <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
  return apiClient.post<T>(url, data, config);
};
const _put = async <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
  return apiClient.put<T>(url, data, config);
};
const _update = async <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
  return apiClient.patch<T>(url, data, config);
};
const _delete = async <T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
  return apiClient.delete<T>(url, config);
};

export { _get, _post, _put, _update, _delete };